import { useState, useMemo, useEffect } from "react";
import { useSeoMeta } from "@unhead/react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  BookOpen,
  Lightbulb,
  Sparkles,
  Zap,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import {
  ALL_LESSONS,
  getLesson,
  getModule,
  type Choice,
} from "@/lib/curriculum";
import { useProgress } from "@/hooks/useProgress";
import { useToast } from "@/hooks/useToast";
import { track } from "@/lib/track";
import NotFound from "./NotFound";

type Step = "learn" | "decide" | "result";

export default function LessonPage() {
  const { lessonId } = useParams<{ lessonId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { completeLesson, isLessonComplete } = useProgress();

  const lesson = getLesson(lessonId ?? "");
  const alreadyDone = lesson ? isLessonComplete(lesson.id) : false;

  const [step, setStep] = useState<Step>("learn");
  const [chosen, setChosen] = useState<Choice | null>(null);

  useSeoMeta({
    title: lesson ? `${lesson.title} — BCH School` : "BCH School",
    description: lesson?.summary,
  });

  const nextLesson = useMemo(() => {
    if (!lesson) return undefined;
    const idx = ALL_LESSONS.findIndex((l) => l.id === lesson.id);
    return ALL_LESSONS[idx + 1];
  }, [lesson]);

  useEffect(() => {
    if (lessonId && getLesson(lessonId)) {
      track("lesson_open", { lessonId });
    }
    // Reset the lesson flow whenever we navigate to a different lesson,
    // so the Learn step (and its image) always shows first.
    setStep("learn");
    setChosen(null);
  }, [lessonId]);

  if (!lesson) return <NotFound />;
  const module = getModule(lesson.moduleId);

  function pick(choice: Choice) {
    setChosen(choice);
    setStep("result");
    if (lesson && !isLessonComplete(lesson.id)) {
      completeLesson(lesson.id, choice.id);
      track("lesson_complete", { lessonId: lesson.id });
      toast({
        title: `+${lesson.xp} XP earned! ⚡`,
        description: `You completed "${lesson.title}".`,
      });
    }
  }

  return (
    <Layout>
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to={`/module/${lesson.moduleId}`}>
          <ArrowLeft className="mr-1 h-4 w-4" /> {module?.title}
        </Link>
      </Button>

      {/* Step indicator */}
      <div className="mb-6 flex items-center gap-2">
        <StepDot active={step === "learn"} done={step !== "learn"} label="Learn" />
        <div className="h-px flex-1 bg-border" />
        <StepDot
          active={step === "decide"}
          done={step === "result"}
          label="Decide"
        />
        <div className="h-px flex-1 bg-border" />
        <StepDot active={step === "result"} done={false} label="Result" />
      </div>

      <div className="mb-2 flex items-center gap-2">
        <span className="flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary">
          <Zap className="h-3 w-3 fill-current" /> {lesson.xp} XP
        </span>
        {alreadyDone && (
          <span className="rounded-full bg-secondary px-2.5 py-1 text-xs font-semibold text-muted-foreground">
            Already completed
          </span>
        )}
      </div>
      <h1 className="mb-6 text-3xl font-extrabold tracking-tight">
        {lesson.title}
      </h1>

      {/* STEP 1 — LEARN */}
      {step === "learn" && (
        <div className="space-y-6">
          <Card className="overflow-hidden border-2 pt-0">
            {module && (
              <div className="aspect-[21/9] w-full overflow-hidden">
                <img
                  src={module.image}
                  alt={`${module.title} illustration`}
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              </div>
            )}
            <CardContent className="space-y-4 px-6 pb-6 pt-2">
              <div className="flex items-center gap-2 text-primary">
                <BookOpen className="h-5 w-5" />
                <h2 className="font-bold uppercase tracking-wide">Learn</h2>
              </div>
              {lesson.learn.map((p, i) => (
                <p key={i} className="text-base leading-relaxed sm:text-lg">
                  {p}
                </p>
              ))}
              {lesson.factBox && (
                <div className="grid gap-2 rounded-2xl bg-secondary/60 p-4 sm:grid-cols-3">
                  {lesson.factBox.map((f) => (
                    <div key={f.label}>
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {f.label}
                      </div>
                      <div className="font-bold">{f.value}</div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
          <Button
            size="lg"
            className="w-full text-base sm:w-auto"
            onClick={() => setStep("decide")}
          >
            Got it — make a decision <ArrowRight className="ml-1 h-5 w-5" />
          </Button>
        </div>
      )}

      {/* STEP 2 — DECIDE */}
      {step === "decide" && (
        <div className="space-y-6">
          <Card className="border-2 border-primary/30 bg-primary/5">
            <CardContent className="space-y-3 p-6">
              <div className="flex items-center gap-2 text-primary">
                <Sparkles className="h-5 w-5" />
                <h2 className="font-bold uppercase tracking-wide">
                  Make a decision
                </h2>
              </div>
              <p className="text-base leading-relaxed sm:text-lg">
                {lesson.scenario}
              </p>
            </CardContent>
          </Card>
          <div className="space-y-3">
            {lesson.choices.map((choice) => (
              <button
                key={choice.id}
                onClick={() => pick(choice)}
                className="group flex w-full items-center gap-3 rounded-2xl border-2 bg-card p-4 text-left font-medium transition-all hover:-translate-y-0.5 hover:border-primary hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-secondary text-sm font-bold uppercase text-muted-foreground group-hover:bg-primary group-hover:text-primary-foreground">
                  {choice.id}
                </span>
                <span>{choice.label}</span>
              </button>
            ))}
          </div>
          <Button variant="ghost" onClick={() => setStep("learn")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Re-read the lesson
          </Button>
        </div>
      )}

      {/* STEP 3 — RESULT + WHY */}
      {step === "result" && chosen && (
        <div className="space-y-6">
          <Card
            className={cn(
              "border-2",
              chosen.outcome === "good" &&
                "border-primary/40 bg-primary/5",
              chosen.outcome === "bad" &&
                "border-destructive/40 bg-destructive/5",
              chosen.outcome === "neutral" && "border-border bg-secondary/40",
            )}
          >
            <CardContent className="space-y-2 p-6">
              <h2 className="font-bold uppercase tracking-wide text-muted-foreground">
                See the result
              </h2>
              <p className="text-base leading-relaxed sm:text-lg">
                {chosen.result}
              </p>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardContent className="space-y-2 p-6">
              <div className="flex items-center gap-2 text-primary">
                <Lightbulb className="h-5 w-5" />
                <h2 className="font-bold uppercase tracking-wide">
                  Understand why
                </h2>
              </div>
              <p className="text-base leading-relaxed sm:text-lg">
                {lesson.why}
              </p>
            </CardContent>
          </Card>

          <div className="flex flex-wrap gap-3">
            {!chosen.correct && (
              <Button
                variant="outline"
                size="lg"
                onClick={() => {
                  setChosen(null);
                  setStep("decide");
                }}
              >
                Try another choice
              </Button>
            )}
            {nextLesson ? (
              <Button
                size="lg"
                className="text-base"
                onClick={() => {
                  setChosen(null);
                  setStep("learn");
                  navigate(`/lesson/${nextLesson.id}`);
                }}
              >
                Next lesson <ArrowRight className="ml-1 h-5 w-5" />
              </Button>
            ) : (
              <Button asChild size="lg" className="text-base">
                <Link to="/quiz">
                  Take the final quiz 🧠
                </Link>
              </Button>
            )}
            <Button asChild variant="ghost" size="lg">
              <Link to={`/module/${lesson.moduleId}`}>Back to module</Link>
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
}

function StepDot({
  active,
  done,
  label,
}: {
  active: boolean;
  done: boolean;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <span
        className={cn(
          "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold transition-colors",
          active && "bg-primary text-primary-foreground",
          done && "bg-primary/30 text-primary",
          !active && !done && "bg-secondary text-muted-foreground",
        )}
      >
        {done ? "✓" : label[0]}
      </span>
      <span
        className={cn(
          "text-xs font-semibold uppercase tracking-wide",
          active ? "text-foreground" : "text-muted-foreground",
        )}
      >
        {label}
      </span>
    </div>
  );
}
